// Traffic awareness — "where is the nearest car ahead of me on the
// racing line?" Uses arc-length difference along the racing line, not
// direction-based projection — direction-based misses cars around
// corners (the next car is off your heading axis even though they're
// dead ahead on the racing line).
//
// The AI controller uses this to slow when closing on a slower car.
// Future passes layer on:
//   - lateral offset awareness (am I on a different line — overtake?)
//   - "behind me" queries for defense behavior
//   - dice-roll-driven dive-bomb commits.

import { forwardArcGap } from '../../shared/racing-line.js';

// Don't bother with cars more than this far ahead — they're not in
// "do I need to brake for them?" territory yet, and including them
// makes the result less useful (the FAR ahead car wins over the
// NEAR ahead car if a car has just lapped you).
const MAX_LOOKAHEAD_M = 80;

// Nearest car physically alongside `self`, or null if no one's close.
// "Alongside" is defined in the AI's heading frame: a car within ~2
// car lengths fore/aft and ~1 car width + safety pad laterally.
// "Nearest" picks the smallest absolute lateral offset — the rival
// most likely to clip if either car drifts.
//
// Used by the AI controller after an overtake: once the slow car
// drops out of nearestCarAhead (the AI's arc has surpassed theirs),
// arc-length-based traffic awareness reports nothing ahead, and the
// lookahead offset would clear immediately. But physically the slow
// car is still right beside the AI. This helper catches that, and
// returning the actual car (vs. just a boolean) lets the controller
// also read the rival's edge proximity for neighbor courtesy.
export function nearestCarAlongside(self, world) {
  const carLen   = self.spec.carLength;
  const carWidth = self.spec.carWidth;
  // Generous "still alongside" window: 2 car lengths past
  // (negative fwd) before the AI is "clear". The over-allowance
  // costs minimal lap time but kills the swerve-back-into-the-car
  // case that looked like a phantom turn-in.
  const fwdMin = -2.0 * carLen;
  const fwdMax =  1.5 * carLen;
  const latMax = carWidth + 1.5;

  const cos = Math.cos(self.heading);
  const sin = Math.sin(self.heading);
  let best = null;
  let bestAbsLat = Infinity;
  for (const other of world.cars) {
    if (other === self) continue;
    if (other._recovered) continue;
    if (other._retired)  continue;
    const dx = other.pos.x - self.pos.x;
    const dy = other.pos.y - self.pos.y;
    const fwd =  dx * cos + dy * sin;
    const lat = -dx * sin + dy * cos;
    if (fwd > fwdMin && fwd < fwdMax && Math.abs(lat) < latMax) {
      const absLat = Math.abs(lat);
      if (absLat < bestAbsLat) {
        bestAbsLat = absLat;
        best = other;
      }
    }
  }
  return best;
}

// Boolean wrapper kept for callers that only need "anyone there?".
export function isAnyCarAlongside(self, world) {
  return nearestCarAlongside(self, world) !== null;
}

// Returns { car, gap } for the nearest car ahead of `self` along the
// racing line, or null if no eligible car exists. `gap` is the
// forward arc-length distance in meters.
//
// Reads each car's per-tick arc cache (car.derived.lineArc) populated
// in main.js's pre-controller pass. Was O(N²) per tick when each
// car's AI redundantly recomputed every other car's nearest line
// point; now O(N) per car total.
export function nearestCarAhead(self, world) {
  const line = world.geometry.racingLine;
  if (!line) return null;
  if (world.cars.length < 2) return null;
  if (self.derived.lineSegIdx < 0) return null;

  // Arc comparisons are only meaningful between cars on the same line.
  // A pitting car's lineArc lives on the pit route; a racing car's on
  // the racing line. Skip cross-line comparisons — the AI doesn't need
  // to "see" cars on the other path through this gate (pit-lane
  // following / overtaking can come later when boxes are modeled).
  const selfPhase = self.pitState?.phase ?? 'racing';
  const myArc = self.derived.lineArc;
  let bestGap = MAX_LOOKAHEAD_M;
  let bestCar = null;
  for (const other of world.cars) {
    if (other === self) continue;
    if (other._recovered) continue;
    // Retired cars aren't traffic — they're obstacles being recovered.
    // Treating them as the "slow car ahead" makes the bunch dominoes
    // behind a wreck under caution and drags racing-pace cars to
    // wreck speed outside caution. The collision system + walls still
    // prevent driving through them; this just keeps them out of the
    // pure-pursuit / follow-match decision.
    if (other._retired) continue;
    if (other.derived.lineSegIdx < 0) continue;
    const otherPhase = other.pitState?.phase ?? 'racing';
    if (otherPhase !== selfPhase) continue;
    const totalLen = selfPhase === 'pitting'
      ? world.geometry.pitRoute?.totalLength ?? line.totalLength
      : line.totalLength;
    const gap = forwardArcGap(myArc, other.derived.lineArc, totalLen);
    // gap === 0 would happen if two cars share a dense index exactly —
    // unlikely but treat as "behind me" rather than "right on top".
    if (gap > 0 && gap < bestGap) {
      bestGap = gap;
      bestCar = other;
    }
  }
  return bestCar ? { car: bestCar, gap: bestGap } : null;
}
